<template>
  <VueFinalModal
    content-transition="vfm-fade"
    :click-to-close="!store.saving"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-2xl bg-[#fafbfc] rounded-[32px] shadow-2xl overflow-hidden z-50 ring-1 ring-black/5"
  >
    <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/60">
      <div>
        <div class="text-[10px] font-bold uppercase tracking-wider text-[#1c4848]/40 mb-1">Postavke / AI modeli / {{ isNew ? 'Novi' : 'Nova verzija' }}</div>
        <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">{{ form.name || form.key || 'Model' }}</h2>
      </div>
      <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] hover:bg-[#1c4848]/5" :disabled="store.saving" @click="close">
        Zatvori
      </button>
    </header>

    <form class="p-8 space-y-6" @submit.prevent="submit">
      <div class="grid grid-cols-2 gap-4">
        <label class="block">
          <span class="text-[11px] font-bold uppercase tracking-wider text-[#1c4848]/50">Ključ</span>
          <input
            v-model.trim="form.key"
            :disabled="!isNew"
            type="text"
            placeholder="hutba-small-q5"
            class="mt-1 w-full px-4 py-2.5 bg-white border border-[#1c4848]/10 rounded-xl text-sm font-mono text-[#1c4848] outline-none focus:border-[#1c4848]/30 disabled:opacity-60"
          >
          <span class="text-[10px] text-[#1c4848]/40">Isti ključ koji aplikacija traži. Mala slova, brojevi, crtice.</span>
        </label>
        <label class="block">
          <span class="text-[11px] font-bold uppercase tracking-wider text-[#1c4848]/50">Verzija</span>
          <input
            v-model.number="form.version"
            type="number"
            min="1"
            class="mt-1 w-full px-4 py-2.5 bg-white border border-[#1c4848]/10 rounded-xl text-sm text-[#1c4848] outline-none focus:border-[#1c4848]/30"
          >
          <span class="text-[10px] text-[#1c4848]/40">Veća od instalirane → aplikacija nudi „Ažuriraj“.</span>
        </label>
      </div>
      <label class="block">
        <span class="text-[11px] font-bold uppercase tracking-wider text-[#1c4848]/50">Naziv</span>
        <input
          v-model.trim="form.name"
          type="text"
          placeholder="Model za hutbu (offline)"
          class="mt-1 w-full px-4 py-2.5 bg-white border border-[#1c4848]/10 rounded-xl text-sm text-[#1c4848] outline-none focus:border-[#1c4848]/30"
        >
      </label>

      <div
        class="rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors"
        :class="dragging ? 'border-[#1c4848] bg-[#1c4848]/5' : 'border-[#1c4848]/15 bg-white'"
        @click="fileInput?.click()"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <input ref="fileInput" type="file" class="hidden" accept=".bin,.mlmodel,.gguf" @change="onPick">
        <template v-if="form.file">
          <p class="text-sm font-bold text-[#1c4848]">{{ form.file.name }}</p>
          <p class="text-xs text-[#1c4848]/50 mt-1">{{ prettySize(form.file.size) }} · klikni da promijeniš</p>
        </template>
        <template v-else>
          <p class="text-sm font-semibold text-[#1c4848]">Prevuci fajl ovdje ili klikni</p>
          <p class="text-xs text-[#1c4848]/50 mt-1">.bin (whisper ggml) ili .mlmodel (CoreML). Do 1 GB. sha256 i veličinu računa server.</p>
        </template>
      </div>

      <div v-if="store.saving" class="space-y-2">
        <div class="h-2 rounded-full bg-[#1c4848]/10 overflow-hidden">
          <div class="h-full bg-[#1c4848] transition-all" :style="{ width: store.uploadProgress + '%' }"></div>
        </div>
        <p class="text-xs text-[#1c4848]/60">
          {{ store.uploadProgress < 100 ? `Šaljem… ${store.uploadProgress} %` : 'Server računa hash i šalje fajl na Space…' }}
        </p>
      </div>
      <p v-if="store.error" class="text-xs text-red-600">{{ store.error }}</p>

      <div class="flex justify-end gap-3">
        <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:bg-[#1c4848]/5" :disabled="store.saving" @click="close">Odustani</button>
        <button
          type="submit"
          :disabled="!canSubmit"
          class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isNew ? 'Objavi model' : `Objavi v${form.version}` }}
        </button>
      </div>
    </form>
  </VueFinalModal>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { useAiModelsStore } from '@/store/ai-models/index.js';

const props = defineProps({
  /** Existing row (new version) or null (brand new key). */
  model: { type: Object, default: null },
  /** Preset for a known key that has no row yet. */
  preset: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);

const store = useAiModelsStore();
const fileInput = ref(null);
const dragging = ref(false);
const isNew = computed(() => !props.model);

const form = reactive({
  key: props.model?.key ?? props.preset?.key ?? '',
  name: props.model?.name ?? props.preset?.name ?? '',
  version: props.model ? Number(props.model.version) + 1 : 1,
  file: null,
});

const canSubmit = computed(() =>
  !store.saving && /^[a-z0-9-]+$/.test(form.key) && form.version >= 1 && form.file,
);

function onPick(e) { form.file = e.target.files?.[0] ?? null; }
function onDrop(e) { dragging.value = false; form.file = e.dataTransfer?.files?.[0] ?? null; }
function prettySize(bytes) { return `${(bytes / 1_048_576).toFixed(1)} MB`; }
function close() { emit('close'); }

async function submit() {
  if (!canSubmit.value) return;
  try {
    await store.upload({ key: form.key, name: form.name, version: form.version, file: form.file });
    emit('saved');
    emit('close');
  } catch {
    // store.error is shown in the form
  }
}
</script>
